import * as Yup from 'yup';

const characterWidths = {
  W: 16,
  I: 7,
  M: 15,
  wide: 11, // Average width for wider characters like A-Z, 0-9
  narrow: 5, // Average width for narrower characters like i, l, etc.
  dash: 9, // Width of dash
  space: 4, // Width of space
  default: 8, // Default width for other characters
};

const validateTextWidth = (text, minWidth, maxWidth, additionalRanges = []) => {
  const estimateTextWidth = (text) => {
    let width = 0;

    for (let char of text) {
      if (char === ' ') {
        width += characterWidths['space'];
      } else if (char === '-') {
        width += characterWidths['dash'];
      } else if (char in characterWidths) {
        width += characterWidths[char];
      } else if (/[A-Z0-9]/.test(char)) {
        width += characterWidths['wide'];
      } else if (/[il]/.test(char)) {
        width += characterWidths['narrow'];
      } else {
        width += characterWidths['default'];
      }
    }

    return width;
  };

  const width = estimateTextWidth(text);

  // Check if the width falls into any of the specified ranges
  for (let range of additionalRanges) {
    if (width >= range[0] && width <= range[1]) {
      return false;
    }
  }

  // Check if the width falls into the default range
  if (width >= minWidth && width <= maxWidth) {
    return false;
  }

  return true;
};

const domains = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'aol.com',
  'hotmail.co.uk',
  'hotmail.fr',
  'msn.com',
  'yahoo.fr',
  'wanadoo.fr',
  'orange.fr',
  'comcast.net',
  'yahoo.co.uk',
  'yahoo.com.br',
  'yahoo.co.in',
  'live.com',
  'rediffmail.com',
  'free.fr',
  'gmx.de',
  'web.de',
  'yandex.ru',
  'ymail.com',
  'libero.it',
  'outlook.com',
  'uol.com.br',
  'bol.com.br',
  'mail.ru',
  'cox.net',
  'hotmail.it',
  'sbcglobal.net',
  'sfr.fr',
  'live.fr',
  'verizon.net',
  'live.co.uk',
  'googlemail.com',
  'yahoo.es',
  'ig.com.br',
  'live.nl',
  'bigpond.com',
  'terra.com.br',
  'yahoo.it',
  'neuf.fr',
  'yahoo.de',
  'alice.it',
  'rocketmail.com',
  'att.net',
  'laposte.net',
  'facebook.com',
  'bellsouth.net',
  'yahoo.in',
  'hotmail.es',
  'charter.net',
  'yahoo.ca',
  'yahoo.com.au',
  'rambler.ru',
  'hotmail.de',
  'tiscali.it',
  'shaw.ca',
  'yahoo.co.jp',
  'sky.com',
  'earthlink.net',
  'optonline.net',
  'freenet.de',
  't-online.de',
  'aliceadsl.fr',
  'virgilio.it',
  'home.nl',
  'qq.com',
  'telenet.be',
  'me.com',
  'yahoo.com.ar',
  'tiscali.co.uk',
  'yahoo.com.mx',
  'voila.fr',
  'gmx.net',
  'mail.com',
  'planet.nl',
  'tin.it',
  'live.it',
  'ntlworld.com',
  'arcor.de',
  'yahoo.co.id',
  'frontiernet.net',
  'hetnet.nl',
  'live.com.au',
  'yahoo.com.sg',
  'zonnet.nl',
  'club-internet.fr',
  'juno.com',
  'optusnet.com.au',
  'blueyonder.co.uk',
  'bluewin.ch',
  'skynet.be',
  'sympatico.ca',
  'windstream.net',
  'mac.com',
  'centurytel.net',
  'chello.nl',
  'live.ca',
  'aim.com',
  'bigpond.net.au',
];
const monthRegex =
  '((January|january|February|february|March|march|April|april|May|may|June|june|July|july|August|august|September|september|October|october|November|november|December|december)|(Jan\\.?|jan\\.?|Feb\\.?|feb\\.?|Mar\\.?|mar\\.?|Apr\\.?|apr\\.?|May\\.?|may\\.?|Jun\\.?|jun\\.?|Jul\\.?|jul\\.?|Aug\\.?|aug\\.?|Sep\\.?|sep\\.?|Oct\\.?|oct\\.?|Nov\\.?|nov\\.?|Dec\\.?|dec\\.?))';
const yearRegex = '\\d{4}';
const presentOrCurrentRegex = '(Present|present|Current|current|Now|now)';
const fullDateRegex = `^(${monthRegex}\\s*${yearRegex}|${yearRegex})\\s*[-––]\\s*(${monthRegex}\\s*${yearRegex}|${yearRegex}|${presentOrCurrentRegex})$`;
const fullEducationDateRegex = `^(${monthRegex}\\s*${yearRegex}\\s*[-––]\\s*${monthRegex}\\s*${yearRegex}|${monthRegex}\\s*${yearRegex}|${yearRegex}\\s*[-–]\\s*${yearRegex}|${yearRegex})$`;

const descriptionSchema = Yup.object().shape({
  text: Yup.string()
    .required('Bullet point cannot be empty')
    .test(
      'not-too-long',
      'Please make this bullet point shorter. Ideally, you want your bullet points to be no more than 3 lines.',
      function (value) {
        const trimmedValue = value.trim();
        return !(trimmedValue.length > 755);
      }
    ),
  subDescriptions: Yup.array().of(Yup.string()).required('Sub-bullet point cannot be empty'), // Adjust this as needed
});

const jobSchema = Yup.object().shape({
  companyName: Yup.string().required('Company name is required'),
  jobTitle: Yup.string().required('Job title is required'),
  yearStartYearEnd: Yup.string()
    .matches(
      new RegExp(fullDateRegex),
      'Date Start - Date End is not in the right format. Please use Jan 2005 - Nov 2009 for example.'
    )
    .required('Date Start - Date End is required'),
  location: Yup.string()
    .matches(
      /^((?:\p{L}|\p{M}|\s|[-.])+,\s*(?:\p{L}|\p{M}|\s|[-.])+|Remote|(?:\p{L}|\p{M}|\s|[-.])+,\s*(?:\p{L}|\p{M}|\s|[-.]+) \(Remote\))$/u,
      'Location must be City, State, City, State (Remote), or Remote'
    )
    .required('Location is required'),
  companyDescription: Yup.string().required('Company description is required'),
  descriptions: Yup.array().of(descriptionSchema).min(1, 'At least one bullet point is required'),
});

const projectSchema = Yup.object().shape({
  projectName: Yup.string().required('Project name is required'),
  descriptions: Yup.array().of(descriptionSchema).min(1, 'At least one bullet point is required'),
});

const resumeSchema = Yup.object().shape({
  contactDetails: Yup.object().shape({
    fullName: Yup.string().required('Full name is required'),
    email: Yup.string().when('$skipEmailValidation', {
      // Based on a literal condition (disableEmailValidation)
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) =>
        schema
          .email('Invalid email address')
          .required('Email is required')
          .test('is-not-hotmail', 'Hotmail addresses are not recommended', (value) =>
            value ? !value.toLowerCase().endsWith('@hotmail.com') : true
          )
          .test('is-spelled-incorrectly', 'Did you typo your email address?', (value) => {
            if (!value) return true; // If the value is empty, it's valid.

            const domain = value.split('@')[1]?.toLowerCase();
            return domain ? domains.includes(domain) : true;
          }),
    }),
    phone: Yup.string().required('Phone number is required'),
    address: Yup.string()
      .matches(
        /^((?:\p{L}|\p{M}|\s|[-.])+,\s*(?:\p{L}|\p{M}|\s|[-.])+|Remote|(?:\p{L}|\p{M}|\s|[-.])+,\s*(?:\p{L}|\p{M}|\s|[-.]+) \(Remote\))$/u,
        'Location must be City, State, City, State (Remote), or Remote'
      )
      .required('Location is required'),
    portfolio: Yup.string().nullable().max(255),
    // Add other contact details validation here
  }),
  workHistory: Yup.array().of(jobSchema),
  education: Yup.array().of(
    Yup.object().shape({
      schoolName: Yup.string().required('School name is required'),
      degree: Yup.string().when('schoolName', {
        is: (val) => {
          const normalized = val ? val.toLowerCase() : '';
          return normalized.includes('high school') || normalized.includes('highschool');
        },
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.required('Degree is required'),
      }),
      gpa: Yup.number()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === null || originalValue.trim() === '' ? null : value
        )
        .min(0, 'GPA must be between 0 and 10')
        .max(10, 'GPA must be between 0 and 10')
        .notRequired()
        .typeError('GPA must be a number'),
      yearStartYearEnd: Yup.string()
        .transform((value, originalValue) => (originalValue === '' ? null : value)) // Convert empty string to null
        .nullable() // Allow null/empty values
        .matches(
          new RegExp(fullEducationDateRegex),
          'Date Start - Date End is not in the right format. Please use Jan 2005 - Nov 2009 for example.'
        )
        .test('is-empty-or-valid', 'Date Start - Date End is not in the right format.', (value) => {
          return !value || new RegExp(fullEducationDateRegex).test(value);
        }),
      location: Yup.string()
        .matches(
          /^((?:\p{L}|\p{M}|\s|[-.])+,\s*(?:\p{L}|\p{M}|\s|[-.])+|Remote|(?:\p{L}|\p{M}|\s|[-.])+,\s*(?:\p{L}|\p{M}|\s|[-.]+) \(Remote\))$/u,
          'Location must be "City, State", "City, State (Remote)", or "Remote"'
        )
        .required('Location is required'),
      // Add other education fields validation here
    })
  ),
  projects: Yup.array().of(projectSchema),
  certifications: Yup.array().of(Yup.string().required('Certification is required')),
  interests: Yup.array().of(Yup.string().required('Interest is required')),
  skills: Yup.array().of(Yup.string().required('Skill is required')),
  technologies: Yup.array().of(Yup.string().required('Technology is required')),
});

export { resumeSchema };
